/**
 * FlightAware AeroAPI Integration Service
 * Provides real-time flight tracking, delays, and status updates
 */

import { FLIGHTAWARE_CONFIG } from '../config/flightAware';
import { FLIGHT_AWARE_API_BASE } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';
import { logger } from '../utils/logger';

// Airline name to code mapping
const AIRLINE_CODES = {
  'American Airlines': 'AA',
  'United Airlines': 'UA',
  'Delta Air Lines': 'DL',
  'Southwest Airlines': 'WN',
  'JetBlue Airways': 'B6',
  'Alaska Airlines': 'AS',
  'Spirit Airlines': 'NK',
  'Frontier Airlines': 'F9',
  'Allegiant Air': 'G4',
  'Hawaiian Airlines': 'HA',
  'Virgin America': 'VX'
};

// ── Extracted helpers for formatFlightData / generateFlightAlerts ─────────

/**
 * Determine the enhanced UX status string for a flight.
 * Priority: arrived > en route > taxiing > original status.
 */
function resolveEnhancedStatus(flight) {
  const hasArrived = flight.actual_in ||
    flight.status?.toLowerCase().includes('arrived') ||
    flight.status?.toLowerCase().includes('landed') ||
    flight.status?.toLowerCase().includes('completed');

  if (hasArrived && flight.gate_destination) return 'Arrived at Gate';
  if (hasArrived) return 'Arrived';
  if (flight.actual_off && !hasArrived) return 'En Route';
  if (flight.actual_out && !flight.actual_off && !hasArrived) return 'Taxiing';
  return flight.status;
}

/** Build the departure sub-object from a raw flight. */
function buildDepartureInfo(flight) {
  return {
    airport: flight.origin?.code || 'N/A',
    terminal: flight.terminal_origin,
    gate: flight.gate_origin,
    scheduled: flight.scheduled_out,
    estimated: flight.estimated_out,
    actual: flight.actual_out || flight.actual_departure_time,
    delay: flight.departure_delay ? Math.round(flight.departure_delay / 60) : 0
  };
}

/** Build the arrival sub-object from a raw flight. */
function buildArrivalInfo(flight) {
  return {
    airport: flight.destination?.code || 'N/A',
    terminal: flight.terminal_destination,
    gate: flight.gate_destination,
    scheduled: flight.scheduled_in,
    estimated: flight.estimated_in,
    actual: flight.actual_in || flight.actual_arrival_time,
    delay: flight.arrival_delay ? Math.round(flight.arrival_delay / 60) : 0
  };
}

/** Collect cancellation / diversion alerts. */
function collectStatusAlerts(alerts, parsedFlight) {
  if (parsedFlight.isCancelled) {
    alerts.push({
      type: 'cancellation',
      severity: 'critical',
      message: `Flight ${parsedFlight.flightNumber} has been cancelled`,
      timestamp: new Date().toISOString()
    });
  }
  if (parsedFlight.isDiverted) {
    alerts.push({
      type: 'diversion',
      severity: 'high',
      message: `Flight ${parsedFlight.flightNumber} has been diverted`,
      timestamp: new Date().toISOString()
    });
  }
}

/** Collect departure / arrival delay alerts. */
function collectDelayAlerts(alerts, parsedFlight) {
  if (parsedFlight.departure.delay > 15) {
    alerts.push({
      type: 'delay',
      severity: parsedFlight.departure.delay > 60 ? 'high' : 'medium',
      message: `Flight ${parsedFlight.flightNumber} is delayed by ${parsedFlight.departure.delay} minutes`,
      timestamp: new Date().toISOString(),
      delayMinutes: parsedFlight.departure.delay
    });
  }
  if (parsedFlight.arrival.delay > 15) {
    alerts.push({
      type: 'arrival_delay',
      severity: parsedFlight.arrival.delay > 60 ? 'high' : 'medium',
      message: `Flight ${parsedFlight.flightNumber} arrival delayed by ${parsedFlight.arrival.delay} minutes`,
      timestamp: new Date().toISOString(),
      delayMinutes: parsedFlight.arrival.delay
    });
  }
}

/** Collect gate, terminal, and departure-time change alerts by comparing two snapshots. */
function collectComparisonAlerts(alerts, parsedFlight, previousFlight) {
  // Gate changes
  if (previousFlight.departure.gate !== parsedFlight.departure.gate &&
      parsedFlight.departure.gate && previousFlight.departure.gate) {
    alerts.push({
      type: 'gate_change',
      severity: 'medium',
      message: `Departure gate changed from ${previousFlight.departure.gate} to ${parsedFlight.departure.gate}`,
      timestamp: new Date().toISOString(),
      oldGate: previousFlight.departure.gate,
      newGate: parsedFlight.departure.gate
    });
  }
  if (previousFlight.arrival.gate !== parsedFlight.arrival.gate &&
      parsedFlight.arrival.gate && previousFlight.arrival.gate) {
    alerts.push({
      type: 'arrival_gate_change',
      severity: 'medium',
      message: `Arrival gate changed from ${previousFlight.arrival.gate} to ${parsedFlight.arrival.gate}`,
      timestamp: new Date().toISOString(),
      oldGate: previousFlight.arrival.gate,
      newGate: parsedFlight.arrival.gate
    });
  }
  // Terminal change
  if (previousFlight.departure.terminal !== parsedFlight.departure.terminal &&
      parsedFlight.departure.terminal && previousFlight.departure.terminal) {
    alerts.push({
      type: 'terminal_change',
      severity: 'high',
      message: `Departure terminal changed from ${previousFlight.departure.terminal} to ${parsedFlight.departure.terminal}`,
      timestamp: new Date().toISOString(),
      oldTerminal: previousFlight.departure.terminal,
      newTerminal: parsedFlight.departure.terminal
    });
  }
  // Departure time change
  if (previousFlight.departure.scheduled !== parsedFlight.departure.scheduled) {
    const oldTime = new Date(previousFlight.departure.scheduled);
    const newTime = new Date(parsedFlight.departure.scheduled);
    const timeDiff = Math.abs(newTime - oldTime) / (1000 * 60);
    if (timeDiff > 5) {
      alerts.push({
        type: 'departure_time_change',
        severity: timeDiff > 30 ? 'high' : 'medium',
        message: `Departure time changed from ${oldTime.toLocaleTimeString()} to ${newTime.toLocaleTimeString()}`,
        timestamp: new Date().toISOString(),
        oldTime: previousFlight.departure.scheduled,
        newTime: parsedFlight.departure.scheduled,
        timeDifferenceMinutes: timeDiff
      });
    }
  }
}

class FlightAwareService {
  constructor(apiKey) {
    this.apiKey = apiKey || FLIGHTAWARE_CONFIG.API_KEY;
    this.baseURL = FLIGHT_AWARE_API_BASE; // Our server-side proxy
  }

  /**
   * Convert airline name to airline code
   * @param {string} airlineName - Full airline name
   * @returns {string} Airline code
   */
  getAirlineCode(airlineName) {
    return AIRLINE_CODES[airlineName] || airlineName;
  }

  /**
   * Get flight status by flight identifier
   * @param {string} flightIdent - Flight identifier (e.g., "AA123", "UAL456")
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   */
  async getFlightStatus(flightIdent, startDate, endDate) {
    try {
      // Use our server-side proxy
      const url = `${this.baseURL}/${encodeURIComponent(flightIdent)}?start=${startDate}&end=${endDate}`;
      
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`FlightAware API error: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('Error fetching flight status:', error);
      throw error;
    }
  }

  /**
   * Get real-time flight tracking data
   * @param {string} flightIdent - Flight identifier
   */
  async getFlightTracking(flightIdent) {
    try {
      const response = await authFetch(
        `${this.baseURL}/flights/${flightIdent}/track`,
        {
          headers: {
            'x-apikey': this.apiKey,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`FlightAware tracking error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching flight tracking:', error);
      throw error;
    }
  }

  /**
   * Get airport delays and conditions
   * @param {string} airportCode - Airport code (e.g., "DFW", "SEA")
   */
  async getAirportDelays(airportCode) {
    try {
      const response = await authFetch(
        `${this.baseURL}/airports/${airportCode}/delays`,
        {
          headers: {
            'x-apikey': this.apiKey,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`FlightAware airport delays error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching airport delays:', error);
      throw error;
    }
  }

  /**
   * Get airport weather conditions
   * @param {string} airportCode - Airport code
   */
  async getAirportWeather(airportCode) {
    try {
      const response = await authFetch(
        `${this.baseURL}/airports/${airportCode}/weather`,
        {
          headers: {
            'x-apikey': this.apiKey,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`FlightAware weather error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching airport weather:', error);
      throw error;
    }
  }

  /**
   * Parse flight data and extract key information
   * @param {Object} flightData - Raw flight data from FlightAware
   * @param {Object} targetFlight - Optional target flight context for matching
   */
  parseFlightData(flightData, targetFlight = null) {
    if (!flightData || !flightData.flights || flightData.flights.length === 0) {
      return null;
    }

    // If we have target flight context, try to match by route and/or date
    if (targetFlight) {
      // If airports are provided, do route matching first
      let matchingFlights = flightData.flights;
      
      if (targetFlight.departureAirport) {
        const targetOrigin = targetFlight.departureAirport.toUpperCase();
        const targetDestination = targetFlight.arrivalAirport ? targetFlight.arrivalAirport.toUpperCase() : null;

        // Match using code_iata (3-letter, e.g. "DFW") which is what users enter
        // Fall back to stripping K/C prefix from code if code_iata is unavailable
        const getIATA = (airport) => {
          if (!airport) return '';
          return (airport.code_iata || airport.code || '').toUpperCase().replace(/^[KC]/, '');
        };

        matchingFlights = flightData.flights.filter(flight => {
          const originMatch = getIATA(flight.origin) === targetOrigin;
          if (!originMatch) return false;
          if (targetDestination) {
            return getIATA(flight.destination) === targetDestination;
          }
          return true;
        });

        if (matchingFlights.length === 0) {
          return null;
        }
      }
      // If no airports provided, matchingFlights is already all flights
      
      // Prefer flights that match the user's selected date (local timezone)
      // If departureTime is already in YYYY-MM-DD format, use it directly
      let userDateStr;
      if (typeof targetFlight.departureTime === 'string' && targetFlight.departureTime.match(/^\d{4}-\d{2}-\d{2}$/)) {
        userDateStr = targetFlight.departureTime;
      } else {
        const userSelectedDate = new Date(targetFlight.departureTime);
        userDateStr = userSelectedDate.getFullYear() + '-' + 
          String(userSelectedDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(userSelectedDate.getDate()).padStart(2, '0');
      }
      
      logger.debug('[FlightAware] User selected date:', userDateStr);
      logger.debug('[FlightAware] Matching flights count:', matchingFlights.length);
      
      // Find flights that match the user's selected date (convert UTC flight times to local timezone)
      const sameDayFlights = matchingFlights.filter(flight => {
        const flightTime = new Date(flight.scheduled_out || flight.scheduled_off);
        // Convert UTC flight time to local timezone for comparison
        const flightDateStr = flightTime.getFullYear() + '-' + 
          String(flightTime.getMonth() + 1).padStart(2, '0') + '-' + 
          String(flightTime.getDate()).padStart(2, '0');
        logger.debug('[FlightAware] Flight scheduled_out:', flight.scheduled_out, '→ Local date:', flightDateStr, 'Match:', flightDateStr === userDateStr);
        return flightDateStr === userDateStr;
      });
      
      logger.debug('[FlightAware] Same-day flights found:', sameDayFlights.length);
      
      // Use same-day flight if available, otherwise use upcoming flights, otherwise first matching flight
      let matchingFlight = null;
      if (sameDayFlights.length > 0) {
        // Sort same-day flights by departure time (earliest first)
        sameDayFlights.sort((a, b) => {
          const timeA = new Date(a.scheduled_out || a.scheduled_off || a.estimated_out).getTime();
          const timeB = new Date(b.scheduled_out || b.scheduled_off || b.estimated_out).getTime();
          return timeA - timeB;
        });
        matchingFlight = sameDayFlights[0];
        logger.debug('[FlightAware] Selected earliest same-day flight:', matchingFlight.scheduled_out);
      } else {
        const now = new Date();
        const upcomingFlights = matchingFlights.filter(flight => {
          const scheduledTime = new Date(flight.scheduled_out || flight.scheduled_off);
          return scheduledTime > now && !flight.status?.toLowerCase().includes('arrived');
        });
        matchingFlight = upcomingFlights.length > 0 ? upcomingFlights[0] : matchingFlights[0];
      }
      
      if (matchingFlight) {
        return this.formatFlightData(matchingFlight);
      }
    }

    // Fallback: Find the flight with the most recent departure time and valid gate information
    const flight = flightData.flights.reduce((latest, current) => {
      // Prefer flights with gate information
      if (current.gate_origin && !latest.gate_origin) {
        return current;
      }
      if (!current.gate_origin && latest.gate_origin) {
        return latest;
      }
      
      // If both have gates, prefer the one with the most recent departure time
      if (current.gate_origin && latest.gate_origin) {
        const currentTime = new Date(current.scheduled_out || current.estimated_out);
        const latestTime = new Date(latest.scheduled_out || latest.estimated_out);
        return currentTime > latestTime ? current : latest;
      }
      
      // If neither has gates, prefer the one with more recent departure
      const currentTime = new Date(current.scheduled_out || current.estimated_out);
      const latestTime = new Date(latest.scheduled_out || latest.estimated_out);
      
      return currentTime > latestTime ? current : latest;
    });

    return this.formatFlightData(flight);
  }

  /**
   * Format flight data into consistent structure
   * @param {Object} flight - Raw flight object from FlightAware
   */
  formatFlightData(flight) {
    const enhancedStatus = resolveEnhancedStatus(flight);
    const progressMetrics = this.calculateFlightProgress(flight);

    return {
      flightNumber: flight.ident,
      airline: flight.airline,
      status: enhancedStatus,
      originalStatus: flight.status,
      departure: buildDepartureInfo(flight),
      arrival: buildArrivalInfo(flight),
      aircraft: {
        type: flight.aircraft_type,
        registration: flight.registration
      },
      progress: {
        percent: flight.progress_percent,
        ...progressMetrics
      },
      position: flight.position ? {
        latitude: flight.position.latitude,
        longitude: flight.position.longitude,
        altitude: flight.position.altitude,
        heading: flight.position.heading,
        speed: flight.position.groundspeed
      } : null,
      lastUpdate: flight.last_position_update,
      isDelayed: (flight.departure_delay > 0) || (flight.arrival_delay > 0),
      isCancelled: flight.status === 'Cancelled',
      isDiverted: flight.status === 'Diverted'
    };
  }

  /**
   * Calculate flight progress metrics (elapsed time, remaining time)
   * @param {Object} flight - Raw flight object from FlightAware
   */
  calculateFlightProgress(flight) {
    const now = new Date();
    
    // Determine departure time
    const departureTime = flight.actual_out ? new Date(flight.actual_out) : 
                         flight.estimated_out ? new Date(flight.estimated_out) :
                         flight.scheduled_out ? new Date(flight.scheduled_out) : null;
    
    // Determine arrival time                     
    const arrivalTime = flight.estimated_in ? new Date(flight.estimated_in) : 
                       flight.scheduled_in ? new Date(flight.scheduled_in) : null;
    
    let elapsedTime = null;
    let remainingTime = null;
    let totalFlightTime = null;
    
    if (departureTime && arrivalTime) {
      totalFlightTime = Math.round((arrivalTime - departureTime) / (1000 * 60)); // minutes
      
      // Only show elapsed time if flight has actually departed
      if (now >= departureTime) {
        elapsedTime = Math.round((now - departureTime) / (1000 * 60)); // minutes
      }
      
      remainingTime = Math.max(0, Math.round((arrivalTime - now) / (1000 * 60))); // minutes
    }
    
    return {
      elapsedTime,
      remainingTime,
      totalFlightTime,
      progressPercent: flight.progress_percent,
      hasDeparted: departureTime ? now >= departureTime : false
    };
  }

  /**
   * Get flight alerts and notifications
   * @param {Object} parsedFlight - Parsed flight data
   * @param {Object} previousFlight - Previous flight data for comparison (optional)
   */
  generateFlightAlerts(parsedFlight, previousFlight = null) {
    const alerts = [];

    collectStatusAlerts(alerts, parsedFlight);
    collectDelayAlerts(alerts, parsedFlight);

    if (previousFlight) {
      collectComparisonAlerts(alerts, parsedFlight, previousFlight);
    }

    return alerts;
  }
}

// Export singleton instance
let flightAwareService = null;

export const initializeFlightAware = (apiKey) => {
  if (!flightAwareService) {
    flightAwareService = new FlightAwareService(apiKey);
  }
  return flightAwareService;
};

export const getFlightAwareService = () => {
  if (!flightAwareService) {
    throw new Error('FlightAware service not initialized. Call initializeFlightAware first.');
  }
  return flightAwareService;
};

export default FlightAwareService;

